import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "../../../services/api";
import PatientSearch from "../../doctor/PatientSearch";
import { useToast } from "../../common/ToastProvider";
import Button from "../../common/Button";
import "./LabManager.css";

interface Lab {
  id: number;
  name: string;
  contact?: string;
}

interface CrownMaterialPrice {
  material: string;
  unit_cost: number;
}

interface LabWork {
  id: number;
  name: string;
}

interface LabRate {
  id: number;
  lab_id: number;
  work_id: number;
  unit_cost: number;
  lab_name?: string;
  work_name?: string;
}

interface LabOrder {
  id: number;
  patient_id?: string;
  patient_name?: string;
  lab_id?: number;
  work_id?: number;
  lab_name?: string;
  work_name?: string;
  impression_date?: string;
  eta_date?: string;
  status?: string;
}

interface LabBill {
  id: number;
  lab_id: number;
  lab_name?: string;
  bill_no?: string;
  bill_date?: string;
  total?: number;
  paid?: number;
  balance?: number;
  status?: string;
}

interface LabPaymentHistoryRow {
  id: number;
  lab_name?: string;
  bill_id?: number;
  bill_no?: string;
  bill_date?: string;
  total?: number;
  total_paid?: number;
  receipt_no?: string;
  paid_date?: string;
  amount?: number;
}

interface Patient {
  patient_id?: number | string;
  name?: string;
}

type LabTab = "master" | "mapping" | "orders" | "ledger" | "stats";
type SortField = 'lab_name' | 'work_name' | 'patient_name' | 'eta_date' | 'status';

export default function LabManager() {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<LabTab>("orders");

  const [labs, setLabs] = useState<Lab[]>([]);
  const [works, setWorks] = useState<LabWork[]>([]);
  const [rates, setRates] = useState<LabRate[]>([]);
  const [orders, setOrders] = useState<LabOrder[]>([]);
  const [bills, setBills] = useState<LabBill[]>([]);
  const [stats, setStats] = useState<{ billed: number; paid: number; pending: number; lab_totals: { lab_id: number; lab_name: string; billed: number; paid: number; pending: number }[] }>({ billed: 0, paid: 0, pending: 0, lab_totals: [] });
  const [crownPrices, setCrownPrices] = useState<CrownMaterialPrice[]>([]);
  const [priceForm, setPriceForm] = useState<Record<string, string>>({});
  const [newMaterialName, setNewMaterialName] = useState("");
  const [newMaterialCost, setNewMaterialCost] = useState("");

  const [labForm, setLabForm] = useState({ name: "", contact: "" });
  const [workForm, setWorkForm] = useState({ name: "" });
  const [mapLabId, setMapLabId] = useState("");
  const [mapWorkId, setMapWorkId] = useState("");
  const [mapCost, setMapCost] = useState("");

  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [orderLabId, setOrderLabId] = useState("");
  const [orderWorkId, setOrderWorkId] = useState("");
  const [impressionDate, setImpressionDate] = useState("");
  const [etaDate, setEtaDate] = useState("");

  const [addBillLabId, setAddBillLabId] = useState("");
  const [addBillNo, setAddBillNo] = useState("");
  const [addBillDate, setAddBillDate] = useState("");
  const [addBillTotal, setAddBillTotal] = useState("");

  const [payLabId, setPayLabId] = useState("");
  const [payBillId, setPayBillId] = useState("");
  const [receiptNo, setReceiptNo] = useState("");
  const [paidDate, setPaidDate] = useState("");
  const [payAmount, setPayAmount] = useState("");

  const [historyLabId, setHistoryLabId] = useState("");
  const [paymentHistory, setPaymentHistory] = useState<LabPaymentHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [pendingOnly, setPendingOnly] = useState(false);

  // Table UX states
  const [orderSearchQuery, setOrderSearchQuery] = useState("");
  const [orderSortField, setOrderSortField] = useState<SortField>('eta_date');
  const [orderSortDesc, setOrderSortDesc] = useState<boolean>(true);
  const [orderVisibleCount, setOrderVisibleCount] = useState<number>(10);

  const todayISO = () => new Date().toLocaleDateString("en-CA");

  const formatPatientLabel = (o: LabOrder) => {
    const name = (o.patient_name || "").trim();
    const mobileOrId = (o.patient_id || "").trim();
    if (name && mobileOrId) return `${name} (${mobileOrId})`;
    return name || mobileOrId || "—";
  };

  const loadData = async () => {
    try {
      const [labsRes, worksRes, ratesRes, ordersRes, billsRes, statsRes] = await Promise.all([
        apiGet("/api/manager/labs"),
        apiGet("/api/manager/lab-works"),
        apiGet("/api/manager/lab-rates"),
        apiGet("/api/manager/lab-orders"),
        apiGet("/api/manager/lab-bills"),
        apiGet("/api/manager/lab-stats"),
      ]);
      setLabs(Array.isArray(labsRes) ? (labsRes as Lab[]) : []);
      setWorks(Array.isArray(worksRes) ? (worksRes as LabWork[]) : []);
      setRates(Array.isArray(ratesRes) ? (ratesRes as LabRate[]) : []);
      setOrders(Array.isArray(ordersRes) ? (ordersRes as LabOrder[]) : []);
      setBills(Array.isArray(billsRes) ? (billsRes as LabBill[]) : []);
      const st = statsRes as { billed?: number; paid?: number; pending?: number; lab_totals?: { lab_id: number; lab_name: string; billed: number; paid: number; pending: number }[] };
      setStats({
        billed: Number(st?.billed || 0),
        paid: Number(st?.paid || 0),
        pending: Number(st?.pending || 0),
        lab_totals: Array.isArray(st?.lab_totals) ? st!.lab_totals! : [],
      });
    } catch (err) {
      showToast({ message: "Failed to load lab data.", type: "error" });
    }
  };

  useEffect(() => {
    loadData();
    apiGet("/api/manager/crown-material-prices")
      .then((res: unknown) => {
        const list = Array.isArray(res) ? (res as CrownMaterialPrice[]) : [];
        setCrownPrices(list);
        const map: Record<string, string> = {};
        list.forEach((p) => {
          map[p.material] = String(p.unit_cost ?? 0);
        });
        setPriceForm(map);
      })
      .catch(() => {
        setCrownPrices([]);
        setPriceForm({});
      });
  }, []);

  const saveLab = async () => {
    if (!labForm.name.trim()) return showToast({ message: "Lab name required", type: "warning" });
    try {
      await apiPost("/api/manager/labs", labForm);
      setLabForm({ name: "", contact: "" });
      showToast({ message: "Lab added efficiently.", type: "success" });
      loadData();
    } catch {
      showToast({ message: "Failed to save lab", type: "error" });
    }
  };

  const deleteLab = async (id: number) => {
    if (!window.confirm("Deactivate this lab?")) return;
    try {
      await apiPost("/api/manager/labs/delete", { lab_id: id });
      showToast({ message: "Lab deactivated.", type: "success" });
      loadData();
    } catch {
      showToast({ message: "Failed to deactivate lab", type: "error" });
    }
  };

  const saveWork = async () => {
    if (!workForm.name.trim()) return showToast({ message: "Work name required", type: "warning" });
    try {
      await apiPost("/api/manager/lab-works", workForm);
      setWorkForm({ name: "" });
      showToast({ message: "Lab work saved.", type: "success" });
      loadData();
    } catch {
      showToast({ message: "Failed to save lab work", type: "error" });
    }
  };

  const saveMapping = async () => {
    if (!mapLabId || !mapWorkId) return showToast({ message: "Select lab and work", type: "warning" });
    try {
      await apiPost("/api/manager/lab-rates", {
        lab_id: mapLabId,
        work_id: mapWorkId,
        unit_cost: Number(mapCost || 0),
      });
      showToast({ message: "Mapping saved successfully", type: "success" });
      setMapCost("");
      loadData();
    } catch (e) {
      showToast({ message: "Failed to save mapping", type: "error" });
    }
  };

  const saveOrder = async () => {
    if (!selectedPatient?.patient_id) return showToast({ message: "Select patient", type: "warning" });
    if (!orderLabId || !orderWorkId) return showToast({ message: "Select lab and work", type: "warning" });
    if (!impressionDate || !etaDate) return showToast({ message: "Select impression and delivery dates", type: "warning" });
    
    try {
      await apiPost("/api/manager/lab-orders", {
        patient_id: selectedPatient.patient_id,
        lab_id: orderLabId,
        work_id: orderWorkId,
        impression_date: impressionDate,
        eta_date: etaDate,
      });
      showToast({ message: "Lab order saved successfully.", type: "success" });
      setImpressionDate("");
      setEtaDate("");
      loadData();
    } catch {
      showToast({ message: "Failed to save lab order.", type: "error" });
    }
  };

  const markReceived = async (id: number) => {
    try {
      await apiPost(`/api/manager/lab-orders/${id}/received`, {});
      showToast({ message: "Order marked as received.", type: "success" });
      loadData();
    } catch {
      showToast({ message: "Failed to mark as received.", type: "error" });
    }
  };

  const createPayment = async () => {
    if (!payBillId || !payAmount) return showToast({ message: "Select bill and amount", type: "warning" });
    try {
      await apiPost("/api/manager/lab-payments", {
        bill_id: payBillId,
        receipt_no: receiptNo,
        paid_date: paidDate || undefined,
        amount: Number(payAmount),
      });
      showToast({ message: "Payment recorded successfully", type: "success" });
      setPayBillId("");
      setReceiptNo("");
      setPaidDate("");
      setPayAmount("");
      loadData();
    } catch {
      showToast({ message: "Failed to record payment", type: "error" });
    }
  };

  const materialLabels: Record<string, string> = {
    metal: "Metal Crown",
    ceramic_normal: "Ceramic – Normal",
    ceramic_premium: "Ceramic – Premium",
    zirconia: "Zirconia Crown",
  };

  const saveCrownPrices = async () => {
    try {
      const entries = Object.keys(priceForm).map((material) => ({
        material,
        unit_cost: Number(priceForm[material] || 0),
      }));
      await Promise.all(entries.map((e) => apiPost("/api/manager/crown-material-prices", e)));
      const res = await apiGet("/api/manager/crown-material-prices");
      const list = Array.isArray(res) ? (res as CrownMaterialPrice[]) : [];
      setCrownPrices(list);
      const map: Record<string, string> = {};
      list.forEach((p) => {
        map[p.material] = String(p.unit_cost ?? 0);
      });
      setPriceForm(map);
      showToast({ message: "Prices saved", type: "success" });
    } catch {
      showToast({ message: "Failed to save prices", type: "error" });
    }
  };

  const saveSingleMaterial = async (material: string) => {
    try {
      await apiPost("/api/manager/crown-material-prices", {
        material,
        unit_cost: Number(priceForm[material] || 0),
      });
      const res = await apiGet("/api/manager/crown-material-prices");
      const list = Array.isArray(res) ? (res as CrownMaterialPrice[]) : [];
      setCrownPrices(list);
      const map: Record<string, string> = {};
      list.forEach((p) => {
        map[p.material] = String(p.unit_cost ?? 0);
      });
      setPriceForm(map);
      showToast({ message: "Price updated", type: "success" });
    } catch {
      showToast({ message: "Failed to update price", type: "error" });
    }
  };

  const addNewMaterial = async () => {
    if (!newMaterialName.trim()) return showToast({ message: "Material name required", type: "warning" });
    try {
      await apiPost("/api/manager/crown-material-prices", {
        material: newMaterialName.trim(),
        unit_cost: Number(newMaterialCost || 0),
      });
      setNewMaterialName("");
      setNewMaterialCost("");
      const res = await apiGet("/api/manager/crown-material-prices");
      const list = Array.isArray(res) ? (res as CrownMaterialPrice[]) : [];
      setCrownPrices(list);
      const map: Record<string, string> = {};
      list.forEach((p) => {
        map[p.material] = String(p.unit_cost ?? 0);
      });
      setPriceForm(map);
      showToast({ message: "Material added", type: "success" });
    } catch {
      showToast({ message: "Failed to add material", type: "error" });
    }
  };

  const createBill = async () => {
    if (!addBillLabId || !addBillNo.trim() || !addBillDate || !addBillTotal) return showToast({ message: "Select lab, bill no, bill date, total", type: "warning" });
    try {
      await apiPost("/api/manager/lab-bills", {
        lab_id: addBillLabId,
        bill_no: addBillNo.trim(),
        bill_date: addBillDate,
        total: Number(addBillTotal),
      });
      showToast({ message: "Bill created successfully.", type: "success" });
      setAddBillLabId("");
      setAddBillNo("");
      setAddBillDate("");
      setAddBillTotal("");
      loadData();
    } catch {
      showToast({ message: "Failed to create bill.", type: "error" });
    }
  };

  const fetchPaymentHistory = async (labId: string) => {
    if (!labId) {
      setPaymentHistory([]);
      return;
    }
    setHistoryLoading(true);
    try {
      const res = await apiGet(`/api/manager/lab-payment-history?lab_id=${labId}`);
      setPaymentHistory(Array.isArray(res) ? (res as LabPaymentHistoryRow[]) : []);
    } catch {
      setPaymentHistory([]);
      showToast({ message: "Failed to load payment history.", type: "error" });
    } finally {
      setHistoryLoading(false);
    }
  };

  const rateLookup = useMemo(() => {
    const map: Record<string, number> = {};
    rates.forEach((r) => {
      map[`${r.lab_id}:${r.work_id}`] = Number(r.unit_cost || 0);
    });
    return map;
  }, [rates]);

  const billsForPayment = useMemo(() => {
    if (!payLabId) return [];
    return bills.filter((b) => String(b.lab_id) === String(payLabId) && Number(b.balance ?? 0) > 0);
  }, [bills, payLabId]);

  const ledgerBills = useMemo(() => {
    if (!pendingOnly) return bills;
    return bills.filter((b) => Number(b.balance ?? 0) > 0);
  }, [bills, pendingOnly]);

  const handlePaymentBillChange = (value: string) => {
    setPayBillId(value);
    const bill = billsForPayment.find((b) => String(b.id) === String(value));
    if (bill) {
      setPayAmount(String(bill.balance ?? bill.total ?? ""));
      if (!paidDate) setPaidDate(todayISO());
    }
  };

  useEffect(() => {
    if (!historyLabId) {
      setPaymentHistory([]);
      return;
    }
    fetchPaymentHistory(historyLabId);
  }, [historyLabId]);

  useEffect(() => {
    if (mapLabId && mapWorkId) {
      const key = `${mapLabId}:${mapWorkId}`;
      const existing = rateLookup[key];
      if (existing !== undefined) {
        setMapCost(String(existing));
      } else {
        setMapCost("");
      }
    }
  }, [mapLabId, mapWorkId, rateLookup]);


  // Order Table Sorting and Searching logic
  const handleOrderSort = (field: SortField) => {
    if (orderSortField === field) {
      setOrderSortDesc(!orderSortDesc);
    } else {
      setOrderSortField(field);
      setOrderSortDesc(false);
    }
  };

  const filteredOrders = useMemo(() => {
    let result = [...orders];

    if (orderSearchQuery) {
      const q = orderSearchQuery.toLowerCase();
      result = result.filter(o => 
        (o.patient_name || '').toLowerCase().includes(q) ||
        (o.lab_name || '').toLowerCase().includes(q) ||
        (o.work_name || '').toLowerCase().includes(q)
      );
    }

    result.sort((a, b) => {
      let valA: string | number = '';
      let valB: string | number = '';
      
      switch (orderSortField) {
        case 'lab_name': valA = a.lab_name || ''; valB = b.lab_name || ''; break;
        case 'work_name': valA = a.work_name || ''; valB = b.work_name || ''; break;
        case 'patient_name': valA = a.patient_name || ''; valB = b.patient_name || ''; break;
        case 'status': valA = a.status || ''; valB = b.status || ''; break;
        case 'eta_date': 
          valA = new Date(a.eta_date || 0).getTime(); 
          valB = new Date(b.eta_date || 0).getTime(); 
          break;
      }

      if (valA < valB) return orderSortDesc ? 1 : -1;
      if (valA > valB) return orderSortDesc ? -1 : 1;
      return 0;
    });

    return result;
  }, [orders, orderSearchQuery, orderSortField, orderSortDesc]);

  const visibleOrders = filteredOrders.slice(0, orderVisibleCount);


  const inputStyle = { padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' as const, outline: 'none' };
  const cardStyle = { background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', boxShadow: 'var(--mod-shadow-sm)' };

  return (
    <div className="mod-standalone-window apexo-lab-container" style={{ display: 'flex', flexDirection: 'column', gap: '24px', minHeight: 'calc(100vh - 120px)' }}>
      <div className="mod-window-header" style={{ paddingBottom: 0, borderBottom: 'none', marginBottom: 0 }}>
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>🔬 Lab Operations</h2>
          <p className="mod-text-muted">Manage vendor orders, track incoming work, and review ledgers.</p>
        </div>
      </div>

      <div className="mod-sub-nav">
        <button className={`mod-sub-btn ${activeTab === "orders" ? "active" : ""}`} onClick={() => setActiveTab("orders")}>Active Orders</button>
        <button className={`mod-sub-btn ${activeTab === "ledger" ? "active" : ""}`} onClick={() => setActiveTab("ledger")}>Financial Ledger</button>
        <button className={`mod-sub-btn ${activeTab === "mapping" ? "active" : ""}`} onClick={() => setActiveTab("mapping")}>Lab Mapping</button>
        <button className={`mod-sub-btn ${activeTab === "master" ? "active" : ""}`} onClick={() => setActiveTab("master")}>Labs & Works</button>
        <button className={`mod-sub-btn ${activeTab === "stats" ? "active" : ""}`} onClick={() => setActiveTab("stats")}>Statistics</button>
      </div>

      {activeTab === "orders" && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px', alignItems: 'start' }}>
          
          <div style={{ ...cardStyle }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a', marginBottom: '16px' }}>New Lab Order</h3>
            
            <div className="apexo-inline-form-grid">
              <div className="apexo-form-cell stretch">
                <label className="apexo-form-label">Patient</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <PatientSearch onSelect={(p) => setSelectedPatient(p as Patient)} />
                  {selectedPatient && <span className="apexo-selected-info">{selectedPatient.name}</span>}
                </div>
              </div>

              <div className="apexo-form-cell">
                <label className="apexo-form-label">Lab</label>
                <select style={inputStyle} value={orderLabId} onChange={(e) => setOrderLabId(e.target.value)}>
                  <option value="">Select Lab</option>
                  {labs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>

              <div className="apexo-form-cell">
                <label className="apexo-form-label">Work</label>
                <select style={inputStyle} value={orderWorkId} onChange={(e) => setOrderWorkId(e.target.value)}>
                  <option value="">Select Work</option>
                  {works.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>

              <div className="apexo-form-cell">
                <label className="apexo-form-label">Impression Date</label>
                <input style={inputStyle} type="date" value={impressionDate} onChange={(e) => setImpressionDate(e.target.value)} />
              </div>

              <div className="apexo-form-cell">
                <label className="apexo-form-label">ETA Date</label>
                <input style={inputStyle} type="date" value={etaDate} onChange={(e) => setEtaDate(e.target.value)} />
              </div>
              
              <div className="apexo-form-cell" style={{ justifyContent: 'flex-end' }}>
                <Button variant="primary" onClick={saveOrder}>Save Order</Button>
              </div>
            </div>
            {orderLabId && orderWorkId && (
                <div style={{ fontSize: '0.85rem', color: '#059669', marginTop: '8px' }}>
                Est. Rate: ₹{rateLookup[`${orderLabId}:${orderWorkId}`] ?? 0}
                </div>
            )}
          </div>

          <div style={{ ...cardStyle, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div className="apexo-table-toolbar">
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>Active & Completed Orders</h3>
              <input 
                type="text" 
                placeholder="Search orders..." 
                value={orderSearchQuery}
                onChange={(e) => setOrderSearchQuery(e.target.value)}
                className="apexo-table-search"
              />
            </div>
            
            <div className="apexo-table-container">
              <table className="apexo-data-table">
                <thead>
                  <tr>
                    <th onClick={() => handleOrderSort('patient_name')} className="sortable">
                      Patient {orderSortField === 'patient_name' && (orderSortDesc ? '▼' : '▲')}
                    </th>
                    <th onClick={() => handleOrderSort('lab_name')} className="sortable">
                      Lab {orderSortField === 'lab_name' && (orderSortDesc ? '▼' : '▲')}
                    </th>
                    <th onClick={() => handleOrderSort('work_name')} className="sortable">
                      Work {orderSortField === 'work_name' && (orderSortDesc ? '▼' : '▲')}
                    </th>
                    <th>Impression</th>
                    <th onClick={() => handleOrderSort('eta_date')} className="sortable">
                      ETA {orderSortField === 'eta_date' && (orderSortDesc ? '▼' : '▲')}
                    </th>
                    <th onClick={() => handleOrderSort('status')} className="sortable text-center">
                      Status {orderSortField === 'status' && (orderSortDesc ? '▼' : '▲')}
                    </th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleOrders.map(o => (
                    <tr key={o.id}>
                      <td className="fw-600">{formatPatientLabel(o)}</td>
                      <td>{o.lab_name}</td>
                      <td>{o.work_name}</td>
                      <td>{o.impression_date ? new Date(o.impression_date).toLocaleDateString() : '—'}</td>
                      <td>{o.eta_date ? new Date(o.eta_date).toLocaleDateString() : '—'}</td>
                      <td className="text-center">
                         <span className={`apexo-status-pill ${o.status === 'RECEIVED' ? 'success' : 'warning'}`}>
                            {o.status}
                         </span>
                      </td>
                      <td className="text-right">
                        {o.status !== "RECEIVED" && (
                          <Button variant="outline" size="sm" onClick={() => markReceived(o.id)}>Mark Received</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {visibleOrders.length === 0 && (
                    <tr>
                       <td colSpan={7} className="apexo-table-empty">No orders found.</td>
                    </tr>
                  )}
                </tbody>
              </table>

              {visibleOrders.length < filteredOrders.length && (
                <div className="apexo-table-footer">
                  <span className="apexo-table-meta">Showing {visibleOrders.length} of {filteredOrders.length}</span>
                  <button className="apexo-show-more-btn" onClick={() => setOrderVisibleCount(prev => prev + 10)}>
                    Show More ⬇️
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Other components remain exactly as they were internally */}
      {activeTab === "ledger" && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) minmax(300px, 1fr) minmax(300px, 1fr)', gap: '20px', alignItems: 'start' }}>
          {/* ... keeping Ledger unchanged as requested pattern was just LabManager table UX ... */}
          <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>Add Bill</h3>
            <select style={inputStyle} value={addBillLabId} onChange={(e) => setAddBillLabId(e.target.value)}>
              <option value="">Select Lab</option>
              {labs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <input style={inputStyle} type="text" placeholder="Bill no." value={addBillNo} onChange={(e) => setAddBillNo(e.target.value)} />
            <input style={inputStyle} type="date" value={addBillDate} onChange={(e) => setAddBillDate(e.target.value)} />
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }}>₹</span>
              <input style={{ ...inputStyle, paddingLeft: '28px' }} type="number" placeholder="Amount" value={addBillTotal} onChange={(e) => setAddBillTotal(e.target.value)} />
            </div>
            <Button variant="primary" onClick={createBill}>Save Bill</Button>
          </div>

          <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>Record Payment</h3>
            <select style={inputStyle} value={payLabId} onChange={(e) => { setPayLabId(e.target.value); setPayBillId(""); setPayAmount(""); }}>
              <option value="">Select Lab</option>
              {labs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <select style={inputStyle} value={payBillId} onChange={(e) => handlePaymentBillChange(e.target.value)}>
              <option value="">Select Bill</option>
              {billsForPayment.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.bill_no} (₹{b.balance ?? b.total})
                </option>
              ))}
            </select>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }}>₹</span>
              <input style={{ ...inputStyle, paddingLeft: '28px' }} type="number" placeholder="Amount" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
            </div>
            <input style={inputStyle} type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} />
            <input style={inputStyle} type="text" placeholder="Receipt no." value={receiptNo} onChange={(e) => setReceiptNo(e.target.value)} />
            <Button variant="primary" onClick={createPayment}>Save Payment</Button>
          </div>

          <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>Lab Totals Console</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {stats.lab_totals.length === 0 && <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>No lab totals yet.</div>}
              {stats.lab_totals.map((l) => (
                <div key={l.lab_id} style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '4px' }}>{l.lab_name}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                    <span style={{ color: '#16a34a' }}>Paid: ₹{l.paid.toLocaleString("en-IN")}</span>
                    <span style={{ color: '#dc2626' }}>Pending: ₹{l.pending.toLocaleString("en-IN")}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ ...cardStyle, gridColumn: '1 / -1', minHeight: '300px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>Lab Ledger</h3>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', color: '#475569', cursor: 'pointer' }}>
                <input type="checkbox" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} />
                Pending Only
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
              {ledgerBills.length === 0 ? (
                <div style={{ color: '#94a3b8', fontStyle: 'italic', gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>No bills match criteria.</div>
              ) : (
                ledgerBills.map((b) => (
                  <div key={b.id} style={{ border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', background: '#f8fafc' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <strong style={{ color: '#0f172a' }}>{b.lab_name} • {b.bill_no}</strong>
                      <span style={{ fontSize: '0.75rem', background: b.status === "PAID" ? '#dcfce7' : '#fee2e2', color: b.status === "PAID" ? '#166534' : '#991b1b', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>{b.status}</span>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '12px' }}>Date: {b.bill_date ? new Date(b.bill_date).toLocaleDateString() : "—"}</div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                       <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                         <span style={{ color: '#475569' }}>Total: ₹{b.total}</span>
                         <span style={{ color: '#0f172a', fontWeight: 600 }}>Paid ₹{b.paid || 0}</span>
                       </div>
                       <div style={{ background: '#e2e8f0', height: '6px', borderRadius: '3px', overflow: 'hidden' }}>
                         <div style={{ width: `${Math.min(100, ((b.paid || 0) / Math.max(b.total || 1, 1)) * 100)}%`, height: '100%', background: '#3b82f6' }} />
                       </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "master" && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', alignItems: 'start' }}>
          
          <div style={{ ...cardStyle }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', color: '#0f172a' }}>Dental Labs</h3>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <input style={inputStyle} type="text" placeholder="Lab name" value={labForm.name} onChange={(e) => setLabForm({ ...labForm, name: e.target.value })} />
              <input style={inputStyle} type="text" placeholder="Contact number" value={labForm.contact} onChange={(e) => setLabForm({ ...labForm, contact: e.target.value })} />
              <Button variant="secondary" onClick={saveLab}>Add</Button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {labs.map((l) => (
                <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.95rem' }}>{l.name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{l.contact || 'No contact info'}</div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => deleteLab(l.id)} style={{ color: '#ef4444', borderColor: '#fca5a5' }}>Deactivate</Button>
                </div>
              ))}
            </div>
          </div>

          <div style={{ ...cardStyle }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', color: '#0f172a' }}>Lab Works</h3>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <input style={inputStyle} type="text" placeholder="Work name" value={workForm.name} onChange={(e) => setWorkForm({ name: e.target.value })} />
              <Button variant="secondary" onClick={saveWork}>Add</Button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {works.map((w) => (
                <div key={w.id} style={{ background: '#f8fafc', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', color: '#334155', fontWeight: 500, fontSize: '0.95rem' }}>
                  {w.name}
                </div>
              ))}
            </div>
          </div>

          <div style={{ ...cardStyle }}>
             <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', color: '#0f172a' }}>Patient Price (Crown Materials)</h3>
             <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                {[
                  ...Object.entries(materialLabels).map(([material, label]) => ({ material, label })),
                  ...crownPrices.filter((p) => !materialLabels[p.material]).map((p) => ({ material: p.material, label: `Other - ${p.material}` }))
                ].map(({ material, label }) => (
                  <div key={material} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.9rem', color: '#475569', fontWeight: 500 }}>{label}</span>
                    <div style={{ display: 'flex', gap: '8px', width: '150px' }}>
                      <input style={inputStyle} type="number" value={priceForm[material] ?? ""} onChange={(e) => setPriceForm((prev) => ({ ...prev, [material]: e.target.value }))} />
                      <Button variant="ghost" size="sm" onClick={() => saveSingleMaterial(material)}>✓</Button>
                    </div>
                  </div>
                ))}
             </div>
             
             <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
               <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#334155' }}>Add New Material</h4>
               <input style={inputStyle} type="text" placeholder="New material name" value={newMaterialName} onChange={(e) => setNewMaterialName(e.target.value)} />
               <input style={inputStyle} type="number" placeholder="Unit cost" value={newMaterialCost} onChange={(e) => setNewMaterialCost(e.target.value)} />
               <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                 <Button variant="secondary" onClick={addNewMaterial} fullWidth>Add</Button>
                 <Button variant="primary" onClick={saveCrownPrices} fullWidth>Save All</Button>
               </div>
             </div>
          </div>

        </div>
      )}

      {activeTab === "mapping" && (
        <div style={{ ...cardStyle, maxWidth: '600px', margin: '0 auto', width: '100%' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', color: '#0f172a' }}>Lab Work Mapping</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
            <select style={inputStyle} value={mapLabId} onChange={(e) => setMapLabId(e.target.value)}>
              <option value="">Select Lab</option>
              {labs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <select style={inputStyle} value={mapWorkId} onChange={(e) => setMapWorkId(e.target.value)}>
              <option value="">Select Work</option>
              {works.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }}>₹</span>
              <input style={{ ...inputStyle, paddingLeft: '28px' }} type="number" placeholder="Cost" value={mapCost} onChange={(e) => setMapCost(e.target.value)} />
            </div>
            <Button variant="primary" onClick={saveMapping}>Save Mapping</Button>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {rates.map((r) => (
              <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '0.95rem', color: '#334155' }}>
                  <strong style={{ color: '#0f172a' }}>{r.lab_name}</strong> — {r.work_name}
                </span>
                <strong style={{ color: '#16a34a' }}>₹{r.unit_cost}</strong>
              </div>
            ))}
            {rates.length === 0 && <div style={{ color: '#94a3b8', fontStyle: 'italic', textAlign: 'center' }}>No mappings created yet.</div>}
          </div>
        </div>
      )}

      {activeTab === "stats" && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div style={{ ...cardStyle, background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)', border: 'none' }}>
            <div style={{ fontSize: '0.9rem', color: '#0284c7', fontWeight: 600, textTransform: 'uppercase', marginBottom: '8px' }}>Total Billed</div>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: '#0369a1' }}>₹{stats.billed.toLocaleString("en-IN")}</div>
          </div>
          <div style={{ ...cardStyle, background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)', border: 'none' }}>
            <div style={{ fontSize: '0.9rem', color: '#16a34a', fontWeight: 600, textTransform: 'uppercase', marginBottom: '8px' }}>Total Paid</div>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: '#15803d' }}>₹{stats.paid.toLocaleString("en-IN")}</div>
          </div>
          <div style={{ ...cardStyle, background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)', border: 'none' }}>
            <div style={{ fontSize: '0.9rem', color: '#dc2626', fontWeight: 600, textTransform: 'uppercase', marginBottom: '8px' }}>Pending</div>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: '#b91c1c' }}>₹{stats.pending.toLocaleString("en-IN")}</div>
          </div>
          
          {stats.lab_totals.length > 0 && (
            <div style={{ ...cardStyle, gridColumn: '1 / -1' }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', color: '#0f172a' }}>Lab-wise Breakdowns</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                {stats.lab_totals.map((l) => (
                  <div key={l.lab_id} style={{ display: 'flex', flexDirection: 'column', gap: '8px', background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '1rem' }}>{l.lab_name}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                      <span style={{ color: '#475569' }}>Billed:</span>
                      <strong style={{ color: '#0284c7' }}>₹{l.billed.toLocaleString("en-IN")}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                      <span style={{ color: '#475569' }}>Paid:</span>
                      <strong style={{ color: '#16a34a' }}>₹{l.paid.toLocaleString("en-IN")}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', borderTop: '1px solid #cbd5e1', paddingTop: '8px' }}>
                      <span style={{ color: '#475569', fontWeight: 600 }}>Pending:</span>
                      <strong style={{ color: '#dc2626' }}>₹{l.pending.toLocaleString("en-IN")}</strong>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
